// ModuleShell — the calm, honest placeholder for every Command Center
// module that is not yet powered. ONE generic presentational component,
// reused by dozens of not-yet-powered routes via router.tsx.
//
// It fetches nothing and fabricates nothing. It reads the module's
// declared status from the registry (shell/commandModel.ts) and renders
// an honest status chip + the module's own summary/detail copy. The
// honesty contract lives entirely in the registry: 'powered' /
// 'requires_setup' / 'later_phase'.

import type { CSSProperties } from 'react'
import { SurfaceCard } from '@square/ui'
import type { ModuleDef, ModuleStatus } from '../../shell/commandModel'
import { SHELL_TOKENS, chipStyle, text } from '../../shell/styles'
import RouteHeader from '../RouteHeader'

interface StatusPresentation {
  label: string
  accent: string
}

// Honest status vocabulary — one chip per declared module status.
// 'powered' still routes here only when the powered component is not
// this app's to render; the chip stays honest either way.
const STATUS_PRESENTATION: Record<ModuleStatus, StatusPresentation> = {
  powered: { label: 'Available', accent: SHELL_TOKENS.green },
  requires_setup: { label: 'Requires backend setup', accent: SHELL_TOKENS.gold },
  later_phase: { label: 'Coming in a later phase', accent: SHELL_TOKENS.inkFaint },
}

const detailStyle: CSSProperties = {
  ...text.body,
  marginTop: 14,
  lineHeight: 1.55,
}

const honestLineStyle: CSSProperties = {
  ...text.rowMeta,
  marginTop: 14,
  lineHeight: 1.5,
  color: SHELL_TOKENS.inkFaint,
}

export default function ModuleShell({ def }: { def: ModuleDef }) {
  const presentation = STATUS_PRESENTATION[def.status] ?? STATUS_PRESENTATION.later_phase

  return (
    <div>
      <RouteHeader
        kicker="SQUARE CODE COMMAND CENTER"
        title={def.label}
        description={def.summary}
      />

      <SurfaceCard level={1} accent={presentation.accent}>
        <span style={chipStyle(presentation.accent)}>{presentation.label}</span>

        {def.detail && <p style={detailStyle}>{def.detail}</p>}

        <p style={honestLineStyle}>
          This module is part of the Square Code operating model. It is shell-only
          in this phase — no data is fabricated here.
        </p>
      </SurfaceCard>
    </div>
  )
}
